/* eslint-disable @typescript-eslint/no-explicit-any */
import { string, z } from 'zod'
import { t } from './translations'
import { customErrorMap } from './utils'
import { ISLANDS, SERVICE_TYPES, VALID_PHONE_NUMBER_REGEX, SERVICE_TYPES_IDS, IN_OUT, TIME_REGEX } from '$lib/constants'
import { isUndefined } from './helpers'
import { UsersSourceOptions } from './pocketbase-types'
import { ValidateSpanishID} from './helpers'
const islands: number[] = Object.values(ISLANDS).map((island) => island.id)
const serviceTypes: number[] = Object.values(SERVICE_TYPES).map((st) => st.id)

z.setErrorMap(customErrorMap)

const addContextErrors = (ctx: z.RefinementCtx, data: any, fields: string[]) => {
  for (const [key, value] of Object.entries(data)) {
    if (!fields.includes(key)) continue
    if (value) continue
    ctx.addIssue({
      code: 'custom',
      message: t.get('errors.field_required'),
      path: [key]
    })
  }
}

export const loginFormSchema = z.object({
  email: z.string().email(),
  password: z.string().min(8).max(50)
})
export type LoginForm = typeof loginFormSchema

export const registerFormSchema = z
  .object({
    name: z.string().min(1).max(50),
    last_name: z.string().min(1).max(50),
    prefix: z.string().trim().optional(),
    phone: z.string().min(1).max(50).optional(),
    email: z.string().email(),
    nif: z.string().min(2).max(50).optional(),
    country: z.string().min(2).max(50).optional(),
    source: z.nativeEnum(UsersSourceOptions),
    password: z.string().min(8).max(50),
    passwordConfirm: z.string().min(8).max(50),
    accepts_commercial_comms: z.coerce.boolean(),
    legal_terms: z.boolean(),
    privacy_terms: z.boolean()
  })
  .refine(({ password, passwordConfirm }) => password === passwordConfirm, {
    message: t.get('errors.passwords_dont_match'),
    path: ['passwordConfirm']
  }).refine((data) => {
    if (data.phone && !data.prefix) {
      return false
    }
    return true
  }, {
    message:  t.get('errors.prefix_is_required'),
    path: ['prefix']
  }).refine((data) => {
    if (!data.phone && data.prefix) {
      return false
    }
    return true
  }, {
    message:  t.get('errors.phone_is_required'),
    path: ['phone']
  })
export type RegisterFormSchema = typeof registerFormSchema

export const estimateRequestFormSchema = z.object({
  // Person data
  name: z.string().trim().optional(),
  last_name: z.string().trim().optional(),
  prefix: z.string().trim().optional(),
  phone: z.string().regex(VALID_PHONE_NUMBER_REGEX, t.get('errors.phone')).optional(),
  email: z.string().email().trim(),
  legalTerms: z.boolean(),
  privacyTerms: z.boolean(),

  // Service data
  island: z.number(),
  service_type: z.number(),
  passengers: z.coerce.number().min(1),
  disabled_passengers: z.coerce.number().nonnegative(),
  start_place: z.string(),
  end_place: z.string(),
  departure_datetime: z.coerce.date(),
  arrival_datetime: z.coerce.date().nullable().optional(),
  comments: z.string().nullable().optional()
}).refine((data) => {
  if (data.phone && !data.prefix) {
    return false
  }
  return true
}, {
  message:  t.get('errors.prefix_is_required'),
  path: ['prefix']
}).refine((data) => {
  if (!data.phone && data.prefix) {
    return false
  }
  return true
}, {
  message:  t.get('errors.phone_is_required'),
  path: ['phone']
})
export type EstimateRequestFormSchema = typeof estimateRequestFormSchema

export const lostAndFoundsSchema = z.object({
  // When and where section
  serviceType: z.number().refine((val) => val !== 0, t.get('errors.field_required')),
  island: z.number().refine((val) => val !== 0, t.get('errors.field_required')),
  departurePoint: z.string().trim().min(1, t.get('errors.field_required')),
  destination: z.string().trim().min(1, t.get('errors.field_required')),
  departureDate: z.coerce.date(),
  departureTime: z.string().regex(TIME_REGEX, t.get('errors.invalid_time')),

  //What Lost section
  category: z.number().refine((val) => val !== 0, t.get('errors.field_required')),
  subcategory: z.number().optional(),
  colour: z.string().trim().min(1, t.get('errors.field_required')),
  description: z.string().trim().min(1, t.get('errors.field_required')),
  file: z.any().optional(),

  //Contact data
  name: z.string().trim().min(1, t.get('errors.field_required')),
  surname: z.string().trim().min(1, t.get('errors.field_required')),
  email: z.string().email().trim(),
  prefix: z.string().optional(),
  phone: z.string().regex(VALID_PHONE_NUMBER_REGEX, t.get('errors.phone')).optional(),
  legalTerms: z.boolean(),
  privacyTerms: z.boolean()

}).refine((data) => {
  if (data.phone && !data.prefix) {
    return false
  }
  return true
}, {
  message:  t.get('errors.prefix_is_required'),
  path: ['prefix']
}).refine((data) => {
  if (!data.phone && data.prefix) {
    return false
  }
  return true
}, {
  message:  t.get('errors.phone_is_required'),
  path: ['phone']
})
export type LostAndFoundsSchema = typeof lostAndFoundsSchema

// Unused by the moment. Find a solution for this and store all passengers data
export const bookingDataExtraPassenger = z.object({
  // Extra passenger data
  name: z.string().trim().optional(),
  lastName: z.string().trim().optional(),
  phone: z.string().regex(VALID_PHONE_NUMBER_REGEX, t.get('errors.phone')).optional(),
  email: z.string().email().trim().optional()
})

export const bookingDataFormSchema = z
  .object({
    // Person data
    name: z.string().trim().min(1, t.get('errors.field_required')),
    lastName: z.string().trim().min(1, t.get('errors.field_required')),
    prefix: z.string().optional(),
    phone: z.string().regex(VALID_PHONE_NUMBER_REGEX, t.get('errors.phone')).optional(),
    email: z.string().email().trim().min(1, t.get('errors.field_required')),
    nif: z.string().trim().optional(),
    address: z.string().trim().optional(),
    postalCode: z.string().trim().optional(),
    country: z.string().trim().optional(),
    // Billing data
    billRequired: z.coerce.number().optional(),
    billingType: z.string().optional(),
    billingName: z.string().optional(),
    billingCountry: z.string().optional(),
    billingAddress: z.string().optional(),
    billingCity: z.string().optional(),
    billingPostalCode: z.coerce.number().optional(),
    billingCif: z.string().optional(),
    billingPhone: z.string().optional(),
    billingProvince: z.string().optional(),
    billingIsland: z.string().optional(),
    billingRelationId: z.number().optional(),

    // Unused by the moment. Find a solution for this and store all passengers data
    // Extra passenger data
    passengers: z.array(bookingDataExtraPassenger).optional(),
    paxNames: z.string().optional(),

    // Service data
    island: z.coerce.number().refine((i) => islands.includes(i), t.get('errors.island')),
    serviceType: z.number().refine((st) => serviceTypes.includes(st), t.get('errors.service_type')),
    paxAdults: z.coerce.number().min(1, t.get('errors.field_required')).nonnegative(),
    paxChildren: z.coerce.number().min(0, t.get('errors.field_required')).nonnegative(),
    paxInfants: z.coerce.number().min(0, t.get('errors.field_required')).nonnegative(),
    paxPMR: z.coerce.number().min(0).max(2, t.get('errors.max_pmr_paxes')).nonnegative().optional(),
    legal_terms: z.boolean(),
    privacy_terms: z.boolean(),

    bike: z.coerce.number().min(0).nonnegative().default(0),
    special: z.coerce.number().min(0).nonnegative().default(0),
    golf: z.coerce.number().min(0).nonnegative().default(0),
    surf: z.coerce.number().min(0).nonnegative().default(0),
    babySeats: z.coerce.number().min(0).nonnegative().default(0),

    tripType: z
      .string()
      .default('one_way')
      .refine((tt) => ['one_way', 'round_trip'].includes(tt), t.get('errors.trip_type')),
    arrivalPlace: z.string(),
    arrivalFlightNumber: z.string().nullable().optional(),
    arrivalDate: z.string().optional(),
    arrivalTime: z.string().optional(),
    departurePlace: z.string(),
    departureFlightNumber: z.string().nullable().optional(),
    departureDate: z.string().optional(),
    pickupStartDate: z.string().optional(),
    departureTime: z.string().optional(),
    endDate: z.string().optional(),
    endTime: z.string().optional(),
    pickupStartTime: z.string().optional(),
    pickupStartDateReturn: z.string().optional(),
    pickupStartTimeReturn: z.string().optional(),
    observations: z.string().optional(),
    route: z.string().optional(),

    // Hidden fields
    inOut: z.string().optional(),
    vehicleType: z.string().optional(),
    startLatitude: z.string().optional(),
    startLongitude: z.string().optional(),
    endLatitude: z.string().optional(),
    endLongitude: z.string().optional(),
    startPlaceId: z.coerce.number().optional(),
    startPlace: z.string().optional(),
    startMeetingPoint: z.string().optional(),
    endPlaceId: z.coerce.number().optional(),
    endPlace: z.string().optional(),
    endMeetingPoint: z.string().optional()
  })
  .superRefine((data, ctx) => {
    const isRoundTrip = data.tripType === 'round_trip'

    if (data.phone && !data.prefix) {
      ctx.addIssue({
        code: 'custom',
        message:  t.get('errors.prefix_is_required'),
        path: ['prefix']
      })
    }
    if (!data.phone && data.prefix) {
      ctx.addIssue({
        code: 'custom',
        message:  t.get('errors.phone_is_required'),
        path: ['phone']
      })
    }
    if (data.inOut && data.inOut !== 'Other') {
      if (data.inOut === 'In') {
        if (!data.arrivalDate || !data.arrivalTime || !data.arrivalFlightNumber) {
          addContextErrors(ctx, data, ['arrivalDate', 'arrivalTime', 'arrivalFlightNumber'])
        }
        if (isRoundTrip) {
          if (data.serviceType == SERVICE_TYPES_IDS.private && (!data.pickupStartDateReturn || !data.pickupStartTimeReturn)) {
            addContextErrors(ctx, data, ['pickupStartDateReturn', 'pickupStartTimeReturn'])
          }
          if (
            data.serviceType == SERVICE_TYPES_IDS.private &&
            data.departureDate &&
            new Date(data.departureDate + ' ' + data.departureTime) <
              new Date(data.pickupStartDateReturn + ' ' + data.pickupStartTimeReturn)
          ) {
            ctx.addIssue({
              code: 'custom',
              message: t.get('errors.departure_pickup_difference'),
              path: ['pickupStartDateReturn']
            })
          }
          if (!data.departureDate) {
            addContextErrors(ctx, data, ['departureDate'])
          }
          if (!data.departureFlightNumber) {
            addContextErrors(ctx, data, ['departureFlightNumber'])
          }
        }
      }

      if (data.inOut === 'Out') {
        if (!data.departureDate) {
          addContextErrors(ctx, data, ['departureDate'])
        }
        if (!data.departureFlightNumber) {
          addContextErrors(ctx, data, ['departureFlightNumber'])
        }
        if (data.serviceType == SERVICE_TYPES_IDS.private && !data.pickupStartDate) {
          addContextErrors(ctx, data, ['pickupStartDate'])
        }
        if (
          data.serviceType == SERVICE_TYPES_IDS.private &&
          data.departureDate &&
          new Date(data.departureDate + ' ' + data.departureTime) < new Date(data.pickupStartDate + ' ' + data.pickupStartTime)
        ) {
          ctx.addIssue({
            code: 'custom',
            message: t.get('errors.departure_pickup_difference'),
            path: ['pickupStartDate']
          })
        }
        if (
          data.serviceType == SERVICE_TYPES_IDS.atDisposal &&
          data.departureDate &&
          new Date(data.departureDate + ' ' + data.departureTime) > new Date(data.endDate + ' ' + data.endTime)
        ) {
          ctx.addIssue({
            code: 'custom',
            message: t.get('errors.disposal_end_time_difference'),
            path: ['departureDate']
          })
        }
        if (isRoundTrip) {
          if (!data.arrivalDate || !data.arrivalTime || !data.arrivalFlightNumber) {
            addContextErrors(ctx, data, ['arrivalDate', 'arrivalTime', 'arrivalFlightNumber'])
          }
        }
      }
    }

    if (data.inOut === 'Other') {
      if (data.tripType !== 'round_trip' && (!data.pickupStartDate || !data.pickupStartTime)) {
        addContextErrors(ctx, data, ['pickupStartDate', 'pickupStartTime'])
      }
      if (isRoundTrip) {
        if (!data.pickupStartDate || !data.pickupStartTime || !data.pickupStartTimeReturn || !data.pickupStartDateReturn) {
          addContextErrors(ctx, data, ['pickupStartDate', 'pickupStartTime', 'pickupStartDateReturn', 'pickupStartTimeReturn'])
        }
      }
      if (
        data.serviceType == SERVICE_TYPES_IDS.atDisposal &&
        data.pickupStartDate &&
        new Date(data.pickupStartDate + ' ' + data.pickupStartTime) > new Date(data.endDate + ' ' + data.endTime)
      ) {
        ctx.addIssue({
          code: 'custom',
          message: t.get('errors.disposal_end_time_difference'),
          path: ['pickupStartDate']
        })
      }
    }

    const inOrRoundTrip = data.inOut === IN_OUT.IN || isRoundTrip
    if (inOrRoundTrip && data.arrivalDate && !data.arrivalTime) {
      addContextErrors(ctx, data, ['arrivalTime'])
    }

    const outOrRoundTrip = data.inOut === IN_OUT.OUT || isRoundTrip
    if (outOrRoundTrip && data.departureDate && !data.departureTime) {
      addContextErrors(ctx, data, ['departureTime'])
    }

    if (data.pickupStartDate && !data.pickupStartTime) {
      addContextErrors(ctx, data, ['pickupStartTime'])
    }

    const otherAndRoundTrip = data.inOut === IN_OUT.OTHER && isRoundTrip
    if (otherAndRoundTrip && data.pickupStartDateReturn && !data.pickupStartTimeReturn) {
      addContextErrors(ctx, data, ['pickupStartTimeReturn'])
    }
    if (data.billRequired && !data.billingRelationId && !data.billingCif) {
      ctx.addIssue({
        code: 'custom',
        message: t.get('errors.field_required'),
        path: ['billingRelationId']
      })
    }
    if (data.billRequired && !data.billingRelationId) {
      if (!data.billingCity) {
        addContextErrors(ctx, data, ['billingCity'])
      }
      if (!data.billingAddress) {
        addContextErrors(ctx, data, ['billingAddress'])
      }
      if (!data.billingPhone) {
        addContextErrors(ctx, data, ['billingPhone'])
      }
      if (!data.billingCif) {
        addContextErrors(ctx, data, ['billingCif'])
      }
      if(data.billingCif.length > 20) {
        ctx.addIssue({
          code: 'custom',
          message: t.get('errors.invalid_cif'),
          path: ['billingCif']
        })
      }
      if (data.billingCountry == 'ES') {
        if (data.billingCif && !ValidateSpanishID(data.billingCif).valid) {
          ctx.addIssue({
            code: 'custom',
            message: t.get('errors.invalid_cif'),
            path: ['billingCif']
          })
        }
        if (
          !data.billingPostalCode ||
          isNaN(data.billingPostalCode) ||
          !/^(0[1-9][0-9]{3}|[1-4][0-9]{4}|5[0-2][0-9]{3})$/.test(data.billingPostalCode as unknown as string)
        ) {
          addContextErrors(ctx, data, ['billingPostalCode'])
        }
      }
    }
  })

export const billingAddressSchema = z
  .object({
    name: z.string().trim().min(1, t.get('errors.field_required')),
    phone: z.string().regex(/^[0-9]{6,15}$/, t.get('errors.phone')),
    address: z.string().trim().min(1, t.get('errors.field_required')),
    zipCode: z.coerce.number().optional(),
    city: z.string().trim().min(1, t.get('errors.field_required')),
    cif: z
      .string()
      .trim()
      .refine((cif) => ValidateSpanishID(cif).valid, {
        message: t.get('errors.invalid_cif')
      }),
    country: z
      .string()
      .trim()
      .default('ES'),
    province: z.string().optional(),
    island: z.string().optional(),
    clientType: z.enum(['individual', 'business']).default('individual')
  })
  .superRefine((data, ctx) => {
    // Validación de provincia y código postal
    if (data.country === 'ES') {
      if (!data.zipCode || !/^(0[1-9][0-9]{3}|[1-4][0-9]{4}|5[0-2][0-9]{3})$/.test(data.zipCode as unknown as string)) {
        addContextErrors(ctx, data, ['zipCode'])
      }

    }
  })

export type BookingDataFormSchema = typeof bookingDataFormSchema
export type BillingDataFormSchema = typeof billingAddressSchema

export const paymentFormSchema = z.object({
  price: z.number().optional(),
  insurancePrice: z.number().optional(),
  insuranceChecked: z.boolean().optional(),
  coupon: z.string().optional(),
  legal_terms: z.boolean(),
  privacy_terms: z.boolean()
})
export type PaymentForm = typeof paymentFormSchema
